/**
 * pageChromeContext — a lightweight channel for a route to publish its
 * top-of-page chrome UP into the global AppShell `.phead`, so the canonical
 * breadcrumb AND the page identity (title · chips · meta) live in ONE place for
 * every page instead of inside a separate, working-area sticky header.
 *
 * Two payloads ride the same channel:
 *   1) `breadcrumb` — the resolved trail (e.g. Work › Projects › {project
 *      name}). List pages resolve it from the route table; detail pages publish
 *      their resolved trail here. AppShell renders it in the top-left `.crumb`.
 *   2) identity `{ title, chips, meta }` — the page's subject line. On a detail
 *      page that is the client name (`title`), the VP · tier pill (`chips`) and
 *      the money chip + assignee (`meta`). AppShell renders it in the VACANT
 *      centre of `.phead`, so the identity no longer needs its own sticky strip
 *      in the working area (owner ask 2026-06-28 — consolidate identity into the
 *      top chrome bar to free the work surface).
 *
 * Why a context: AppShell is global chrome rendered ABOVE the route <Outlet>,
 * so it cannot know a detail page's resolved entity name (e.g. the project's
 * display name) or its live money figures. A detail page calls
 * `usePageChrome({...})` on mount to publish; AppShell reads it via
 * `usePageChromeBreadcrumb()` + `usePageChromeIdentity()` and renders it. The
 * page clears everything on unmount, so the chrome falls back to its default
 * (route-derived) behaviour automatically.
 *
 * Reusable by design: any future detail page (property, lead, payment, task,
 * inbox) can publish its own trail + identity with the same one-line hook — the
 * owner's "replicate the model elsewhere" intent.
 *
 * Mounting: the provider MUST wrap both AppShell and the route tree. It is
 * mounted around <BrowserRouter> in App.tsx (so AppShell + every route are
 * inside it). A crumb with an `href` renders as a <Link>, which needs Router
 * context — that lives in AppShell, which is itself inside BrowserRouter, so
 * the provider itself takes no Router dependency (it only holds state).
 */
import {
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** One crumb in the trail. `href` present → a link; absent → the current page. */
export interface BreadcrumbSegment {
  label: string;
  href?: string;
}

/** The page-identity payload AppShell renders in the `.phead` vacant centre. */
export interface PageChromeIdentity {
  /** Primary subject line (e.g. client name). Rendered in var(--font-display). */
  title?: ReactNode;
  /** Inline chips after the title (e.g. VP · tier pill). */
  chips?: ReactNode;
  /** Compact secondary content (e.g. the money chip + assignee). */
  meta?: ReactNode;
}

/** The full chrome a route publishes. Any field may be omitted. */
export interface PageChromeInput extends PageChromeIdentity {
  breadcrumb?: BreadcrumbSegment[] | null;
}

interface PageChromeState {
  breadcrumb: BreadcrumbSegment[] | null;
  title: ReactNode;
  chips: ReactNode;
  meta: ReactNode;
}

interface PageChromeContextValue {
  chrome: PageChromeState | null;
  setChrome: (next: PageChromeState | null) => void;
}

const PageChromeContext = createContext<PageChromeContextValue | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<PageChromeState | null>(null);
  const value = useMemo(() => ({ chrome, setChrome }), [chrome]);
  return (
    <PageChromeContext.Provider value={value}>
      {children}
    </PageChromeContext.Provider>
  );
}

/**
 * Reader for AppShell — the breadcrumb trail a route has published, or `null`
 * when none is set (AppShell then falls back to its route-derived crumb).
 */
export function usePageChromeBreadcrumb(): BreadcrumbSegment[] | null {
  const ctx = useContext(PageChromeContext);
  return ctx?.chrome?.breadcrumb ?? null;
}

/**
 * Reader for AppShell — the page identity (title · chips · meta) a route has
 * published, or `null` when none is set (AppShell then renders its default
 * route <h1>). Returns `null` rather than an empty object when nothing
 * identity-shaped was published, so AppShell can branch on a single truthy check.
 */
export function usePageChromeIdentity(): PageChromeIdentity | null {
  const ctx = useContext(PageChromeContext);
  const chrome = ctx?.chrome;
  if (!chrome) return null;
  if (chrome.title == null && chrome.chips == null && chrome.meta == null) {
    return null;
  }
  return { title: chrome.title, chips: chrome.chips, meta: chrome.meta };
}

/**
 * Content signature for a ReactNode — concatenates its string/number leaves by
 * walking the element tree. Used as part of the publish effect's structural key
 * so the chrome re-publishes ONLY when the displayed text actually changes
 * (e.g. the money chip's Fee/Paid/Due figures), never on every render (a fresh
 * JSX literal each render has a new identity but the same content). ReactNodes
 * are not JSON-serialisable, so this is the structural-key equivalent of the
 * breadcrumb's JSON.stringify.
 */
function nodeSignature(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeSignature).join('');
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    return nodeSignature(props.children);
  }
  return '';
}

/**
 * Combined publisher — sets the breadcrumb + identity on mount, clears on
 * unmount. Pass `null` to publish nothing. Re-publishes only when the CONTENT
 * changes (breadcrumb compared structurally via JSON; title/chips/meta compared
 * via their string/number leaves), so a page can pass fresh array/JSX literals
 * each render without thrashing the chrome. The latest nodes are read from a ref
 * at publish time, so a re-publish always carries the current render's JSX.
 */
export function usePageChrome(payload: PageChromeInput | null): void {
  const ctx = useContext(PageChromeContext);
  const setChrome = ctx?.setChrome;

  const state: PageChromeState | null = payload
    ? {
        breadcrumb: payload.breadcrumb ?? null,
        title: payload.title ?? null,
        chips: payload.chips ?? null,
        meta: payload.meta ?? null,
      }
    : null;

  // Latest nodes — read at publish time so a re-publish carries current JSX.
  const stateRef = useRef<PageChromeState | null>(state);
  stateRef.current = state;

  // Structural key → stable effect dependency. Breadcrumb is serialisable;
  // title/chips/meta reduce to their visible text via nodeSignature.
  const key = state
    ? [
        JSON.stringify(state.breadcrumb),
        nodeSignature(state.title),
        nodeSignature(state.chips),
        nodeSignature(state.meta),
      ].join('')
    : null;

  useEffect(() => {
    if (!setChrome) return;
    setChrome(stateRef.current);
    return () => setChrome(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setChrome]);
}

/**
 * Back-compat publisher — sets ONLY the breadcrumb trail (clears on unmount).
 * Kept so existing callers and future detail pages that need just a trail can
 * use the one-liner; it delegates to {@link usePageChrome}.
 */
export function usePageBreadcrumb(segments: BreadcrumbSegment[] | null): void {
  usePageChrome(segments ? { breadcrumb: segments } : null);
}
